// Simulator tab on the avatar page: call the avatar from the browser and hear
// the real bot answer. The call runs the production bot code (SIM_MODE) with
// Moonshine STT — same engine, timing and AM detection as a live call — but
// nothing reaches Vicidial or the call logs. Finished test calls are kept in
// simulator_calls (history below), with a stereo recording.
import { useState, useEffect, useRef, useCallback } from 'react'
import {
    Phone, PhoneOff, Mic, MicOff, Send, Loader2, Volume2, Trash2, ChevronDown, ChevronRight,
    AlertCircle, CheckCircle2, RefreshCw, Headphones, Keyboard
} from 'lucide-react'
import { simulatorApi, simulatorWsUrl } from '../../services/api'
import { startSimAudio } from './simAudio'

const DISPO = {
    'transfer-call': ['Transfer', 'tw-text-green-400 tw-bg-green-500/15'],
    'not-interested': ['Not Interested', 'tw-text-amber-300 tw-bg-amber-500/15'],
    'no-answer': ['No Answer', 'tw-text-slate-300 tw-bg-slate-500/15'],
    'answering-machine': ['Voicemail', 'tw-text-violet-300 tw-bg-violet-500/15'],
    'do-not-call': ['DNC', 'tw-text-red-300 tw-bg-red-500/15'],
    'age-do-not-qualify': ['Age DQ', 'tw-text-red-300 tw-bg-red-500/15'],
    'call-back': ['Call Back', 'tw-text-cyan-300 tw-bg-cyan-500/15'],
    'busy': ['Busy', 'tw-text-violet-300 tw-bg-violet-500/15']
}
function DispoBadge({ value }) {
    const [label, cls] = DISPO[value] || [value || '—', 'tw-text-slate-300 tw-bg-slate-500/15']
    return <span className={`tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${cls}`}>{label}</span>
}

const ROLE = {
    caller: { label: 'You', cls: 'tw-text-sky-300' },
    bot: { label: 'Bot', cls: 'tw-text-fg' },
    match: { label: 'Match', cls: 'tw-text-violet-300' },
    system: { label: 'System', cls: 'tw-text-muted' }
}
function EventLine({ e }) {
    const r = ROLE[e.role] || ROLE.system
    return (
        <div className="tw-flex tw-gap-3 tw-py-1 tw-text-sm">
            <span className="tw-w-12 tw-shrink-0 tw-text-right tw-font-mono tw-text-xs tw-text-muted">{(e.t ?? 0).toFixed(1)}s</span>
            <span className="tw-w-10 tw-shrink-0 tw-text-xs tw-text-muted">S{e.step ?? '–'}</span>
            <span className={`tw-w-14 tw-shrink-0 tw-text-xs tw-font-semibold ${r.cls}`}>{r.label}{e.typed ? ' ⌨' : ''}</span>
            <span className={`tw-min-w-0 tw-flex-1 tw-break-words ${e.role === 'system' ? 'tw-text-muted' : 'tw-text-fg-secondary'}`}>
                {e.text}{e.mid ? <span className="tw-ml-1 tw-text-xs tw-text-amber-300">(during bot audio)</span> : null}
            </span>
        </div>
    )
}

function Meter({ level, color }) {
    return (
        <div className="tw-h-1.5 tw-w-24 tw-overflow-hidden tw-rounded-full tw-bg-black/30">
            <div className={`tw-h-full tw-transition-[width] tw-duration-75 ${color}`} style={{ width: `${Math.min(100, Math.round(level * 140))}%` }} />
        </div>
    )
}

export default function SimulatorTab({ avatarId, userName }) {
    const [status, setStatus] = useState(null)          // { bot_up, active_sessions, max_sessions }
    const [phase, setPhase] = useState('idle')          // idle | connecting | loading | live | ending
    const [info, setInfo] = useState(null)              // 'ready' payload
    const [events, setEvents] = useState([])
    const [partial, setPartial] = useState('')
    const [disposition, setDisposition] = useState(null)
    const [error, setError] = useState(null)
    const [micLevel, setMicLevel] = useState(0)
    const [botLevel, setBotLevel] = useState(0)
    const [muted, setMuted] = useState(false)
    const [typed, setTyped] = useState('')
    const [elapsed, setElapsed] = useState(0)
    const [history, setHistory] = useState([])
    const [openCall, setOpenCall] = useState(null)      // { id, events } expanded in history
    const wsRef = useRef(null)
    const audioRef = useRef(null)
    const startRef = useRef(0)
    const logRef = useRef(null)

    const loadStatus = useCallback(() => simulatorApi.status().then(r => setStatus(r.data)).catch(() => setStatus({ bot_up: false })), [])
    const loadHistory = useCallback(() => simulatorApi.listCalls(avatarId).then(r => setHistory(r.data.calls || [])).catch(() => {}), [avatarId])
    useEffect(() => { loadStatus(); loadHistory() }, [loadStatus, loadHistory])
    useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight }, [events, partial])
    useEffect(() => {
        if (phase !== 'live') return
        const t = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 500)
        return () => clearInterval(t)
    }, [phase])
    useEffect(() => () => teardown(), [])   // leaving the page ends the call

    function teardown() {
        try { audioRef.current?.stop() } catch (_) {}
        audioRef.current = null
        const ws = wsRef.current
        wsRef.current = null
        if (ws && ws.readyState <= 1) { try { ws.send(JSON.stringify({ type: 'hangup' })) } catch (_) {} try { ws.close() } catch (_) {} }
        setMicLevel(0); setBotLevel(0)
    }

    async function startCall() {
        setError(null); setEvents([]); setPartial(''); setDisposition(null); setInfo(null); setElapsed(0); setMuted(false)
        setPhase('connecting')
        let audio
        try {
            audio = await startSimAudio({
                onPcm: (buf) => { const ws = wsRef.current; if (ws && ws.readyState === 1) ws.send(buf) },
                onMicLevel: setMicLevel,
                onBotLevel: setBotLevel
            })
        } catch (e) {
            setPhase('idle')
            setError(e.name === 'NotAllowedError' ? 'Microphone permission was denied — allow it in the browser and try again.' : e.message)
            return
        }
        audioRef.current = audio
        const ws = new WebSocket(simulatorWsUrl(avatarId, userName))
        ws.binaryType = 'arraybuffer'
        wsRef.current = ws
        ws.onmessage = (m) => {
            if (typeof m.data !== 'string') { audioRef.current?.play(m.data); return }
            let msg; try { msg = JSON.parse(m.data) } catch (_) { return }
            if (msg.type === 'loading') setPhase('loading')
            else if (msg.type === 'ready') { setInfo(msg); setPhase('live'); startRef.current = Date.now() }
            else if (msg.type === 'event') {
                setEvents(ev => [...ev, msg.event])
                if (msg.event.role === 'caller') setPartial('')
            }
            else if (msg.type === 'partial') setPartial(msg.text)
            else if (msg.type === 'disposition') setDisposition(msg.disposition)
            else if (msg.type === 'error') setError(msg.message)
            else if (msg.type === 'ended') {
                setDisposition(msg.disposition)
                setPhase('idle')
                teardown()
                loadHistory(); loadStatus()
            }
        }
        ws.onerror = () => setError('Lost the connection to the simulator.')
        ws.onclose = () => { if (wsRef.current === ws) { setPhase('idle'); teardown(); loadHistory(); loadStatus() } }
    }

    function hangUp() {
        setPhase('ending')
        const ws = wsRef.current
        if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: 'hangup' }))
        else teardown()
    }

    function sendTyped(e) {
        e.preventDefault()
        const t = typed.trim()
        const ws = wsRef.current
        if (!t || !ws || ws.readyState !== 1 || phase !== 'live') return
        ws.send(JSON.stringify({ type: 'text', text: t }))
        setTyped('')
    }

    function toggleMute() { const m = !muted; setMuted(m); audioRef.current?.setMuted(m) }

    async function toggleCall(c) {
        if (openCall?.id === c.id) { setOpenCall(null); return }
        try {
            const r = await simulatorApi.getCall(c.id)
            setOpenCall({ id: c.id, events: r.data.call.events || [] })
        } catch (_) { setOpenCall({ id: c.id, events: [] }) }
    }
    async function deleteCall(c) {
        if (!window.confirm('Delete this test call and its recording?')) return
        await simulatorApi.deleteCall(c.id).catch(() => {})
        if (openCall?.id === c.id) setOpenCall(null)
        loadHistory()
    }
    async function clearAll() {
        if (!window.confirm(`Delete all ${history.length} test calls for this avatar?`)) return
        await simulatorApi.clearCalls(avatarId).catch(() => {})
        setOpenCall(null); loadHistory()
    }

    const busy = phase === 'connecting' || phase === 'loading' || phase === 'ending'
    const inCall = phase !== 'idle'
    const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {/* status + explanation */}
            <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card tw-p-4">
                <div className="tw-min-w-0 tw-flex-1 tw-text-sm tw-text-muted">
                    Call this avatar from your browser and talk to the <strong className="tw-text-fg">real bot</strong> — same engine, timing,
                    Moonshine transcription and answering-machine detection as a live call. Nothing is sent to Vicidial or counted in call logs.
                    <span className="tw-ml-1 tw-inline-flex tw-items-center tw-gap-1"><Headphones size={13} /> Use headphones so the bot doesn't hear itself.</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
                    {status == null ? <Loader2 size={14} className="spinning" />
                        : status.bot_up
                            ? <span className="tw-flex tw-items-center tw-gap-1 tw-text-green-400"><CheckCircle2 size={14} /> Simulator ready · {status.active_sessions}/{status.max_sessions} in use</span>
                            : <span className="tw-flex tw-items-center tw-gap-1 tw-text-red-300"><AlertCircle size={14} /> Simulator bot is not running on this server</span>}
                    <button className="btn btn-icon btn-sm" onClick={loadStatus} title="Refresh status"><RefreshCw size={13} /></button>
                </div>
            </div>

            {/* call panel */}
            <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-px-4 tw-py-3">
                    {!inCall ? (
                        <button className="btn btn-primary" onClick={startCall} disabled={status && !status.bot_up}>
                            <Phone size={15} /> Start test call
                        </button>
                    ) : (
                        <button className="btn btn-danger" onClick={hangUp} disabled={phase === 'ending'}>
                            {busy ? <Loader2 size={15} className="spinning" /> : <PhoneOff size={15} />}
                            {phase === 'connecting' ? 'Connecting…' : phase === 'loading' ? 'Loading avatar…' : phase === 'ending' ? 'Ending…' : 'Hang up'}
                        </button>
                    )}
                    {inCall && (
                        <button className="btn btn-secondary btn-sm" onClick={toggleMute} disabled={phase !== 'live'} title={muted ? 'Unmute mic' : 'Mute mic'}>
                            {muted ? <MicOff size={14} /> : <Mic size={14} />} {muted ? 'Muted' : 'Mic on'}
                        </button>
                    )}
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-muted"><Mic size={13} /><Meter level={micLevel} color="tw-bg-sky-400" /></div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-muted"><Volume2 size={13} /><Meter level={botLevel} color="tw-bg-green-400" /></div>
                    <div className="tw-flex-1" />
                    {phase === 'live' && <span className="tw-font-mono tw-text-sm tw-text-fg">{mmss(elapsed)}</span>}
                    {disposition && <DispoBadge value={disposition} />}
                </div>
                {info && (
                    <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-px-4 tw-py-2 tw-text-xs tw-text-muted">
                        <span>Voice set: <strong className="tw-text-fg-secondary">{info.voice_set || '—'}</strong></span>
                        <span>{info.engine_actions} engine actions · {info.keyword_categories} keyword categories · {info.voice_categories} voice categories</span>
                        {info.bot_version && <span>bot {info.bot_version}</span>}
                        {info.incomplete?.length > 0 && <span className="tw-text-amber-300">⚠ incomplete: {info.incomplete.join(', ')}</span>}
                    </div>
                )}
                <div ref={logRef} className="tw-h-80 tw-overflow-auto tw-px-4 tw-py-2">
                    {events.length === 0 && !partial ? (
                        <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-sm tw-text-muted">
                            {phase === 'idle' ? 'Start a test call to see the bot\'s decisions here.' : phase === 'loading' ? 'Loading the avatar\'s script and voice files…' : 'Waiting for the call…'}
                        </div>
                    ) : (<>
                        {events.map((e, i) => <EventLine key={i} e={e} />)}
                        {partial && <div className="tw-py-1 tw-pl-[7.5rem] tw-text-sm tw-italic tw-text-muted">“{partial}…”</div>}
                    </>)}
                </div>
                <form onSubmit={sendTyped} className="tw-flex tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-border tw-px-4 tw-py-3">
                    <div className="tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-border tw-bg-input tw-px-3">
                        <Keyboard size={14} className="tw-text-muted" />
                        <input value={typed} onChange={e => setTyped(e.target.value)} disabled={phase !== 'live'}
                            placeholder={phase === 'live' ? 'Type what the caller says instead of speaking (treated like speech)…' : 'Typing is available during a call'}
                            className="tw-flex-1 tw-border-0 tw-bg-transparent tw-py-2 tw-text-sm tw-text-fg tw-outline-none" />
                    </div>
                    <button type="submit" className="btn btn-secondary" disabled={phase !== 'live' || !typed.trim()}><Send size={14} /> Send</button>
                </form>
                {error && <div className="tw-mx-4 tw-mb-3 tw-rounded-md tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300">{error}</div>}
            </div>

            {/* history */}
            <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card">
                <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
                    <div className="tw-text-sm tw-font-semibold">Recent test calls <span className="tw-font-normal tw-text-muted">({history.length})</span></div>
                    <div className="tw-flex tw-gap-2">
                        <button className="btn btn-icon btn-sm" onClick={loadHistory} title="Refresh"><RefreshCw size={13} /></button>
                        {history.length > 0 && <button className="btn btn-secondary btn-sm" onClick={clearAll}><Trash2 size={13} /> Clear all</button>}
                    </div>
                </div>
                {history.length === 0 ? (
                    <div className="tw-px-4 tw-pb-4 tw-text-sm tw-text-muted">No test calls yet.</div>
                ) : history.map(c => (
                    <div key={c.id} className="tw-border-0 tw-border-t tw-border-solid tw-border-border">
                        <div className="tw-flex tw-cursor-pointer tw-flex-wrap tw-items-center tw-gap-3 tw-px-4 tw-py-2 tw-text-sm hover:tw-bg-white/5" onClick={() => toggleCall(c)}>
                            {openCall?.id === c.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span className="tw-w-36 tw-text-fg-secondary">{new Date(c.started_at).toLocaleString()}</span>
                            <DispoBadge value={c.disposition} />
                            <span className="tw-text-muted">{mmss(c.duration_seconds || 0)} · step {c.final_step ?? '–'} · by {c.hung_up_by || '—'}</span>
                            {c.user_name && <span className="tw-text-xs tw-text-muted">· {c.user_name}</span>}
                            <div className="tw-flex-1" />
                            <button className="btn btn-icon btn-sm" onClick={(e) => { e.stopPropagation(); deleteCall(c) }} title="Delete"><Trash2 size={13} /></button>
                        </div>
                        {openCall?.id === c.id && (
                            <div className="tw-flex tw-flex-col tw-gap-2 tw-bg-black/10 tw-px-4 tw-py-3">
                                {c.recording_filename && <audio controls preload="none" src={simulatorApi.recordingUrl(c.id)} className="tw-w-full" />}
                                <div className="tw-max-h-72 tw-overflow-auto">
                                    {openCall.events.length ? openCall.events.map((e, i) => <EventLine key={i} e={e} />)
                                        : <div className="tw-text-sm tw-text-muted">No events recorded.</div>}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}
